/**
 * Issues-tab bodies for the ExceptionDrawer. ``view`` picks which one:
 *   - ``'open'``  : the open-issues list, or the healthy empty states ("No open
 *                   issues" / "Nothing to fix here" / "No tracked exceptions").
 *                   The object bulk actions live on the tab row, not here.
 *   - ``'closed'``: a flat list of the recently resolved/ignored entries, or
 *                   the "No closed issues" empty state. No collapsible
 *                   disclosure - the Closed issues tab IS the disclosure.
 * Purely presentational: every value and callback arrives via props from the
 * drawer's model hook.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import IssuesEntryList from '../../../../config/exceptions/shared/IssuesEntryList'
import type { IssuesChipEntry } from '../../../../config/exceptions/shared/issuesState'
import type { ExceptionDrawerDetailBlock } from '../../../../config/exceptions/types'
import type { useDrawerObjectActions } from './useDrawerObjectActions'

interface ExceptionDrawerIssuesBodyProps {
  /** Which list this body renders - the Open issues tab or the Closed issues tab. */
  view: 'open' | 'closed'
  openEntries: IssuesChipEntry[]
  closedEntries: IssuesChipEntry[]
  hasOpen: boolean
  actionsGated: boolean
  objectActions: ReturnType<typeof useDrawerObjectActions>
  listOpenedFrom: string | undefined
  focusedCode: string | undefined
  detailsBlocks: ExceptionDrawerDetailBlock[] | undefined
  fpCode: string
  emptyHealthy: boolean
  rowResetKey: string
}

const ExceptionDrawerIssuesBody: React.FC<ExceptionDrawerIssuesBodyProps> = ({
  view,
  openEntries,
  closedEntries,
  hasOpen,
  actionsGated,
  objectActions,
  listOpenedFrom,
  focusedCode,
  detailsBlocks,
  fpCode,
  emptyHealthy,
  rowResetKey,
}) => {
  // Healthy "nothing to fix" surfaces - a centred icon + message, no card
  // chrome, sitting ~30px below the tab row. ``emptyHealthy`` is the
  // margin-only product (untracked warnings, no tracked tags); the bare
  // "Nothing to fix" and per-tab "No open/closed issues" states share it.
  const healthyCard = (text: string) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        mt: '46px',
        mb: '16px',
        px: '22px',
      }}
    >
      <CheckCircleOutlineIcon
        sx={{ color: 'success.main', fontSize: 20, flexShrink: 0 }}
      />
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{text}</Typography>
    </Box>
  )

  // Shared list wiring for both views - rows expand to their Why/How and
  // (Evo/PSC) carry the status-aware kebab.
  const listProps = {
    fpCode,
    // The drawer's Issues list is always interactive (rows expand to Why/How
    // regardless of whether a focus is set), so a no-focus open (Data Explorer)
    // still expands on click. ``openedFromCode`` only drives auto-expand.
    interactive: true,
    showRowActions: actionsGated,
    onRowAction: actionsGated ? objectActions.onRowAction : undefined,
    openedFromCode: listOpenedFrom,
    actionsPending: objectActions.pending,
    eagerDetailCode: focusedCode,
    eagerDetailBlocks: detailsBlocks,
  }

  // Closed issues tab: the recently resolved/ignored entries as a flat list,
  // or a healthy empty state. The focused closed row auto-expands via
  // ``openedFromCode``.
  if (view === 'closed') {
    if (closedEntries.length === 0) return healthyCard('No closed issues')
    return (
      <IssuesEntryList
        key={`closed-${rowResetKey}`}
        entries={closedEntries}
        {...listProps}
      />
    )
  }

  // Open issues tab. With nothing tracked at all, show the healthy state; an
  // empty open list while closed history exists reads "No open issues" (the
  // user can switch to the Closed issues tab). The bulk-action buttons sit on
  // the tab row above, not in this body.
  if (openEntries.length === 0 && closedEntries.length === 0) {
    return emptyHealthy
      ? healthyCard('No tracked exceptions')
      : healthyCard('Nothing to fix here')
  }

  return hasOpen ? (
    <IssuesEntryList
      key={`open-${rowResetKey}`}
      entries={openEntries}
      {...listProps}
    />
  ) : (
    healthyCard('No open issues')
  )
}

export default ExceptionDrawerIssuesBody
